import React from 'react';

import type { ColumnsV2Props } from '@withjoy/joykit';
import { Box, ColumnsV2 } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { isInIframe } from '@shared/utils/isInIframe';
import { useIsCarbonIframe } from '@shared/hooks/useCarbonIframeLinkProps';
import { StyledBoxBrannan } from './Frames.styles';
import type { FrameOuterProps } from './Frames.types';
import { CustomArt } from '../CustomArt/CustomArt';

export const BrannanFrameMaxWidth = 1024;

const FrameOuter: React.FC<FrameOuterProps> = ({
  applicatorProp,
  children,
  loading,
  customBackgroundColor,
  loadingBackgroundColor,
  customTextColor,
  isCustomPage,
  isCustomArtEnabled,
  customArtUrl,
  themeSprites,
  canvasBackgroundColor,
  ...restProps
}) => {
  const isCarbonIframe = useIsCarbonIframe();
  // Carbon iframe needs clicks to register; affected links open in a new tab via useCarbonIframeLinkProps.
  const isNonCarbonIframe = isInIframe() && !isCarbonIframe;
  return (
    <StyledBoxBrannan
      loading={loading}
      {...applicatorProp}
      transition={animationTransition('background', 'background-color')}
      data-testid={'outer-frame'}
      {...restProps}
      position={'relative'}
      isPreviewing={isNonCarbonIframe}
      customBackgroundColor={customBackgroundColor}
      loadingBackgroundColor={loadingBackgroundColor}
      customTextColor={customTextColor}
      canvasBackgroundColor={canvasBackgroundColor}
    >
      {/* First children — the DOM position the legacy applicator's insertBefore produced. */}
      {themeSprites}
      {isCustomArtEnabled && <CustomArt backgroundImageUrl={customArtUrl} />}
      {/* This overflow clip is necessary to prevent the center part of the page from scrolling */}
      <Box overflow={'clip'}>{children}</Box>
    </StyledBoxBrannan>
  );
};

const FrameInner: React.FC<HtmlProps<HTMLElement> & ColumnsV2Props> = ({ children, ...restProps }) => {
  return (
    <ColumnsV2
      fluid={true}
      maxWidth={[null, null, BrannanFrameMaxWidth]}
      {...restProps}
    >
      <ColumnsV2.Column data-testid={'inner-frame'}>{children}</ColumnsV2.Column>
    </ColumnsV2>
  );
};

export { FrameOuter, FrameInner };
